import type { CalendarRecurrenceRule } from '../types/calendarWorkspace';

export function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
}

export function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addCalendarDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

export function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 0 || day === 6;
}

/** Nth Mon–Fri date in a month (1-based). Returns null if that month has fewer workdays. */
export function nthWorkdayOfMonth(year: number, monthIndex: number, nth: number): Date | null {
  if (nth < 1) return null;
  let count = 0;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day += 1) {
    const d = new Date(year, monthIndex, day, 12, 0, 0, 0);
    if (!isWeekend(d)) {
      count += 1;
      if (count === nth) return d;
    }
  }
  return null;
}

export function daysBetween(aYmd: string, bYmd: string): number {
  const a = parseYmd(aYmd);
  const b = parseYmd(bYmd);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

export function addMonthsClamped(d: Date, months: number): Date {
  const year = d.getFullYear();
  const month = d.getMonth() + months;
  const day = d.getDate();
  const target = new Date(year, month, 1, 12, 0, 0, 0);
  const dim = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, dim));
  return target;
}

export function normalizeRecurrenceRule(
  rule: CalendarRecurrenceRule | null | undefined,
): CalendarRecurrenceRule | null {
  if (!rule || rule.freq === 'none') return null;
  const interval = Math.max(1, Math.floor(Number(rule.interval) || 1));
  if (rule.freq === 'nth_workday') {
    const nth = Math.max(1, Math.min(23, Math.floor(Number(rule.nth) || 1)));
    return {
      freq: 'nth_workday',
      interval: 1,
      nth,
      until: rule.until?.trim() || null,
    };
  }
  if (rule.freq === 'daily' || rule.freq === 'weekly' || rule.freq === 'monthly') {
    return {
      freq: rule.freq,
      interval,
      until: rule.until?.trim() || null,
    };
  }
  return null;
}

export function recurrenceRuleLabel(rule: CalendarRecurrenceRule | null | undefined): string {
  const r = normalizeRecurrenceRule(rule);
  if (!r) return 'Does not repeat';
  let base = 'Does not repeat';
  if (r.freq === 'daily') {
    base = r.interval === 1 ? 'Every day' : `Every ${r.interval} days`;
  } else if (r.freq === 'weekly') {
    base = r.interval === 1 ? 'Every week' : `Every ${r.interval} weeks`;
  } else if (r.freq === 'monthly') {
    base = r.interval === 1 ? 'Every month' : `Every ${r.interval} months`;
  } else if (r.freq === 'nth_workday') {
    const n = r.nth ?? 1;
    const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    base = `${n}${suffix} working day of each month`;
  }
  if (r.until) return `${base} · until ${r.until}`;
  return `${base} · no ending date`;
}

/**
 * Occurrence todo dates for a series, starting at index 0 = startTodoYmd.
 * Caps at `maxCount` occurrences and optional until date.
 */
export function listRecurrenceTodoDates(
  startTodoYmd: string,
  rule: CalendarRecurrenceRule,
  options?: { maxCount?: number; horizonYmd?: string },
): string[] {
  const normalized = normalizeRecurrenceRule(rule);
  if (!normalized) return [startTodoYmd];

  const maxCount = options?.maxCount ?? 52;
  const until = normalized.until ? parseYmd(normalized.until) : null;
  const horizon = options?.horizonYmd ? parseYmd(options.horizonYmd) : null;
  const hardStop = (() => {
    const candidates: Date[] = [];
    if (until) candidates.push(until);
    if (horizon) candidates.push(horizon);
    if (candidates.length === 0) return null;
    return candidates.reduce((a, b) => (a < b ? a : b));
  })();

  const out: string[] = [];
  const start = parseYmd(startTodoYmd);

  if (normalized.freq === 'nth_workday') {
    const nth = normalized.nth ?? 1;
    let year = start.getFullYear();
    let month = start.getMonth();
    // Align first occurrence to the nth workday on/after start month.
    for (let guard = 0; guard < maxCount + 24 && out.length < maxCount; guard += 1) {
      const candidate = nthWorkdayOfMonth(year, month, nth);
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
      if (!candidate) continue;
      if (candidate < start) continue;
      if (hardStop && candidate > hardStop) break;
      out.push(formatYmd(candidate));
    }
    return out.length ? out : [startTodoYmd];
  }

  let cursor = start;
  const interval = Math.max(1, normalized.interval ?? 1);
  for (let i = 0; i < maxCount; i += 1) {
    if (hardStop && cursor > hardStop) break;
    out.push(formatYmd(cursor));
    if (normalized.freq === 'daily') {
      cursor = addCalendarDays(cursor, interval);
    } else if (normalized.freq === 'weekly') {
      cursor = addCalendarDays(cursor, 7 * interval);
    } else if (normalized.freq === 'monthly') {
      cursor = addMonthsClamped(cursor, interval);
    } else {
      break;
    }
  }
  return out;
}
